import styles from "../search-clients.module.css";

import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BaseTable, SortHeader } from "../../../../components/table";
import { LocationCell } from "../../../../components/table/cells/LocationCell";
import { SORT_DIRECTION } from "../../../../components/table/headers/SortHeader";
import AlertSettingsCell from "./cells/alert-settings-cell";
import AlertSettings from "./headers/AlertSettings";
import { ResidentLink } from "../cells/ResidentLink";
import { ClientOverviewStore } from "../../store/store";
import { TransitionStore } from "../../../../store";
import { BasicHeader } from "../../../../components/table/headers/BasicHeader";
import UserSettingsStore, {
    SortSettings,
    UserSettings,
} from "../../../../store/user-settings";

interface AlertOverviewGridProps {
    clientOverviewStore?: ClientOverviewStore;
    transitionStore?: TransitionStore;
    userSettingsStore?: UserSettingsStore;
}

function AlertOverviewGrid(props: AlertOverviewGridProps) {
    const clientOverviewStore = props.clientOverviewStore!;
    const userSettingsStore = props.userSettingsStore!;
    const { t } = useTranslation(["client-grid"]);
    const { nightTimeAlerts } = clientOverviewStore;
    const sortOptions = userSettingsStore.getUserSetting(
        SortSettings.ALARM_OVERVIEW
    );
    const privacyMode = userSettingsStore.getUserSetting(
        UserSettings.PRIVACY_MODE
    );
    const history = useNavigate();

    const getSortDirection = (id: string) => {
        if (sortOptions.id === id) {
            if (sortOptions.desc) {
                return SORT_DIRECTION.DESCENDING;
            } else return SORT_DIRECTION.ASCENDING;
        }
        return SORT_DIRECTION.NONE;
    };

    const getClientNameHeader = () => {
        if (privacyMode) {
            return <BasicHeader text={t("Client")} />;
        }
        return (
            <SortHeader
                text={t("Client")}
                selected={sortOptions.id === "clientName"}
                sortDirection={getSortDirection("clientName")}
                locked={false}
                onClick={() => {
                    if (sortOptions.id === "clientName") {
                        userSettingsStore.setSortSetting(
                            SortSettings.ALARM_OVERVIEW,
                            {
                                id: "clientName",
                                desc: !sortOptions.desc,
                            }
                        );
                    } else {
                        userSettingsStore.setSortSetting(
                            SortSettings.ALARM_OVERVIEW,
                            { id: "clientName", desc: false }
                        );
                    }
                }}
            />
        );
    };

    const columnData: any[] = [
        {
            header: t("Client"),
            cellComponent: (value: any) => (
                <ResidentLink
                    value={value.clientName}
                    click={() => {
                        accessClientProfile(value.id);
                    }}
                    privacyMode={privacyMode}
                />
            ),
            width: "25%",
            HeaderComponent: getClientNameHeader(),
        },
        {
            header: t("Room"),
            cellComponent: LocationCell,
            width: "40%",
            HeaderComponent: (
                <SortHeader
                    text={t("Room")}
                    selected={sortOptions.id === "locationName"}
                    sortDirection={getSortDirection("locationName")}
                    locked={false}
                    onClick={() => {
                        if (sortOptions.id === "locationName") {
                            userSettingsStore.setSortSetting(
                                SortSettings.ALARM_OVERVIEW,
                                {
                                    id: "locationName",
                                    desc: !sortOptions.desc,
                                }
                            );
                        } else {
                            userSettingsStore.setSortSetting(
                                SortSettings.ALARM_OVERVIEW,
                                { id: "locationName", desc: false }
                            );
                        }
                    }}
                />
            ),
        },
        {
            cellComponent: (value: any) => {
                return (
                    <AlertSettingsCell
                        data={value.alertSettings}
                        nightTime={nightTimeAlerts}
                        showTooltip
                    />
                );
            },
            HeaderComponent: <AlertSettings store={clientOverviewStore} />,
        },
    ];

    const accessClientProfile = (clientId: number) => {
        history(`/client-profile/${clientId}`);
    };

    const render = () => {
        const { selectedClient } = clientOverviewStore;
        return (
            <BaseTable
                minWidth={1286}
                data={clientOverviewStore!.alertSettingsList}
                columns={columnData}
                customRowId={(_index, rowData) => {
                    if (rowData.id === selectedClient?.id)
                        return "selected-client";
                    return "";
                }}
                customRowClass={(_index, rowData) => {
                    if (rowData.id === selectedClient?.id)
                        return styles["selected-client"];
                    return "";
                }}
                expandedRowComponent={<div />}
            ></BaseTable>
        );
    };

    return render();
}
export default inject(
    "clientOverviewStore",
    "transitionStore",
    "userSettingsStore"
)(observer(AlertOverviewGrid));
